import './MobileEvents.scss';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useProtectedInterval } from '../../hooks/useProtectedInterval';
import Toast from '../../components/Toast/Toast';
import Pagination from '../../components/Layout/Pagination';
import EmptyState from '../../components/Layout/EmptyState';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { ExclamationTriangleIcon } from '../../components/Layout/icons';

const EVENT_STATUS_STYLES = {
    'closed':         { bg: 'rgba(34,197,94,0.12)',  color: '#16a34a', rowBg: 'rgba(34,197,94,0.03)' },
    'already_closed': { bg: 'rgba(107,114,128,0.12)', color: '#6b7280', rowBg: 'transparent' },
    'invalid':        { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444', rowBg: 'rgba(239,68,68,0.03)' },
    'unknown':        { bg: 'rgba(239,68,68,0.10)',  color: '#dc2626', rowBg: 'transparent' },
    'unrecognized':   { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b', rowBg: 'rgba(245,158,11,0.03)' },
    'notfound':       { bg: 'rgba(245,158,11,0.12)', color: '#d97706', rowBg: 'rgba(245,158,11,0.03)' },
    'confused':       { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b', rowBg: 'rgba(245,158,11,0.03)' },
    'ignored':        { bg: 'rgba(107,114,128,0.10)', color: '#9ca3af', rowBg: 'transparent' },
    'no_top_up':      { bg: 'rgba(107,114,128,0.10)', color: '#9ca3af', rowBg: 'transparent' },
};

const formatEventDate = (isoStr) => {
    if (!isoStr) return { date: '--', time: '' };
    try {
        const d = new Date(isoStr);
        const day = String(d.getDate()).padStart(2, '0');
        const mon = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const h = String(d.getHours()).padStart(2, '0');
        const m = String(d.getMinutes()).padStart(2, '0');
        const s = String(d.getSeconds()).padStart(2, '0');
        return { date: `${day}.${mon}.${year}`, time: `${h}:${m}:${s}` };
    } catch { return { date: isoStr, time: '' }; }
};

export default function MobileEvents() {
    const { t } = useTranslation('events');
    const theme = useSelector((state) => state.APP_THEME);
    const api_url = useSelector((state) => state.API_URL);
    const navigate = useNavigate();

    const [eventsData, setEventsData] = useState({ results: [], count: 0, device_ids: [], banks: [] });
    const [errorFetching, setErrorFetching] = useState(false);
    const [pageSize, setPageSize] = useState(15);
    const [pageIndex, setPageIndex] = useState(1);
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedBank, setSelectedBank] = useState('');
    const [amountSearch, setAmountSearch] = useState('');
    const [showInstruction, setShowInstruction] = useState(false);

    const getEventsData = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        try {
            let url = api_url + `mobile-events/?page=${pageIndex}&page_size=${pageSize}`;
            if (selectedDeviceId) {
                url += `&device_id=${encodeURIComponent(selectedDeviceId)}`;
            }
            if (selectedStatus) {
                url += `&status=${encodeURIComponent(selectedStatus)}`;
            }
            if (selectedBank) {
                url += `&bank=${encodeURIComponent(selectedBank)}`;
            }
            if (amountSearch) {
                url += `&amount=${encodeURIComponent(amountSearch)}`;
            }
            const response = await fetch(url, {
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.clear();
                    navigate('/auth');
                }
                setErrorFetching(true);
                return;
            }
            const data = await response.json();
            setEventsData(data);
        } catch {
            setErrorFetching(true);
        }
    }, [api_url, pageIndex, pageSize, selectedDeviceId, selectedStatus, selectedBank, amountSearch, navigate]);

    useProtectedInterval(getEventsData, [pageSize, pageIndex, selectedDeviceId, selectedStatus, selectedBank, amountSearch]);

    const results = eventsData.results || [];
    const deviceIds = eventsData.device_ids || [];
    const banks = eventsData.banks || [];
    const hasSensitiveWarning = results.some(r => r.has_sensitive_warning);

    const STATUS_OPTIONS = [
        { value: 'closed', label: t('events:statuses.closed') },
        { value: 'already_closed', label: t('events:statuses.already_closed') },
        { value: 'notfound', label: t('events:statuses.notfound') },
        { value: 'confused', label: t('events:statuses.confused') },
        { value: 'unrecognized', label: t('events:statuses.unrecognized') },
        { value: 'ignored', label: t('events:statuses.ignored') },
        { value: 'invalid', label: t('events:statuses.invalid') },
        { value: 'unknown', label: t('events:statuses.unknown') },
        { value: 'no_top_up', label: t('events:statuses.no_top_up') },
    ];

    const handleFilterChange = (setter) => (e) => {
        setter(e.target.value);
        setPageIndex(1);
    };

    return (
        <div className={theme === 'light' ? "MobileEvents" : "MobileEvents dark"}>
            <Toast show={errorFetching} type="error" message={t('events:errors.fetchError')} onClose={() => setErrorFetching(false)} />
            <div className="content">
                <div className="event-filter-row">
                    <div className="event-device-filter">
                        <select value={selectedDeviceId} onChange={handleFilterChange(setSelectedDeviceId)}>
                            <option value="">{t('events:filters.allDevices')}</option>
                            {deviceIds.map(did => (
                                <option key={did} value={did}>{did}</option>
                            ))}
                        </select>
                    </div>
                    <div className="event-device-filter">
                        <select value={selectedStatus} onChange={handleFilterChange(setSelectedStatus)}>
                            <option value="">{t('events:filters.allStatuses')}</option>
                            {STATUS_OPTIONS.map(s => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="event-device-filter">
                        <select value={selectedBank} onChange={handleFilterChange(setSelectedBank)}>
                            <option value="">{t('events:filters.allBanks')}</option>
                            {banks.map(b => (
                                <option key={b} value={b}>{b}</option>
                            ))}
                        </select>
                    </div>
                    <div className="event-amount-search">
                        <input
                            type="number"
                            placeholder={t('events:filters.amount')}
                            value={amountSearch}
                            onChange={(e) => { setAmountSearch(e.target.value); setPageIndex(1); }}
                            onKeyDown={(e) => { if (e.key === 'Escape') { setAmountSearch(''); setPageIndex(1); } }}
                        />
                    </div>
                </div>

                <Pagination
                    pageIndex={pageIndex}
                    totalCount={eventsData.count || 0}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[10, 15, 30]}
                />

                {hasSensitiveWarning && (
                    <div className="event-warning-banner" onClick={() => setShowInstruction(!showInstruction)}>
                        <span className="event-warning-icon"><ExclamationTriangleIcon /></span>
                        <div className="event-warning-content">
                            <div className="event-warning-title">{t('events:warning.title')}</div>
                            <div className="event-warning-desc">{t('events:warning.description')}</div>
                            <span className="event-warning-toggle">
                                {showInstruction ? t('events:warning.hideInstruction') : t('events:warning.showInstruction')}
                            </span>
                            {showInstruction && (
                                <div className="event-warning-instruction">
                                    {t('events:warning.instruction')}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {results.length === 0 ? (
                    <EmptyState title={t('events:empty.title')} description={t('events:empty.description')} />
                ) : (
                    <Paper className="event-table-paper" sx={{ width: '100%', overflow: 'hidden', background: theme === 'light' ? 'white' : 'rgba(31, 38, 48, 1)', borderRadius: '12px' }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="event-th">{t('events:headers.eventType')}</TableCell>
                                        <TableCell className="event-th">{t('events:headers.date')}</TableCell>
                                        <TableCell className="event-th">{t('events:headers.amount')}</TableCell>
                                        <TableCell className="event-th">{t('events:headers.card')}</TableCell>
                                        <TableCell className="event-th">{t('events:headers.status')}</TableCell>
                                        <TableCell className="event-th">{t('events:headers.bank')}</TableCell>
                                        <TableCell className="event-th">{t('events:headers.message')}</TableCell>
                                        <TableCell className="event-th">{t('events:headers.device')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.map((item, index) => {
                                        const st = EVENT_STATUS_STYLES[item.status_raw] || {};
                                        const dt = formatEventDate(item.created);
                                        return (
                                            <TableRow key={item.pk || index} style={{ background: st.rowBg }} className="event-row">
                                                <TableCell>
                                                    <span className="event-type-badge">
                                                        {t('events:eventTypes.' + item.event_type_raw, { defaultValue: item.event_type })}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="event-date">
                                                        <span className="event-date-value">{dt.date}</span>
                                                        <span className="event-date-time">{dt.time}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {item.amount != null ? (
                                                        <>
                                                            <span className="event-amount">{Number(item.amount).toLocaleString('ru-RU')}</span>
                                                        </>
                                                    ) : (
                                                        <span className="event-currency">--</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>{item.card_number || '--'}</TableCell>
                                                <TableCell>
                                                    <span className="event-badge" style={{ background: st.bg, color: st.color }}>
                                                        {t('events:statuses.' + item.status_raw, { defaultValue: item.status })}
                                                    </span>
                                                </TableCell>
                                                <TableCell>{item.bank || '--'}</TableCell>
                                                <TableCell>
                                                    <span className="event-msg" title={item.short_msg}>
                                                        {item.has_sensitive_warning && (
                                                            <span className="event-sensitive-icon" title={t('events:warning.title')}>
                                                                <ExclamationTriangleIcon />
                                                            </span>
                                                        )}
                                                        {' '}{item.short_msg || '--'}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="event-device" title={item.device_id}>{item.device_id || '--'}</span>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                <Pagination
                    pageIndex={pageIndex}
                    totalCount={eventsData.count || 0}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[10, 15, 30]}
                />
            </div>
        </div>
    );
}
